import React, { useState, useEffect, useMemo, useContext, useRef } from 'react'
import { ConfigContext } from 'contexts/ConfigContext'
import { TextField } from '@material-ui/core'
import cloneDeep from 'clone-deep'
import { Button } from 'react-bootstrap'
import objectPath from 'object-path'
import DatePickerCustom from '../../DatePicker/DatePickerCustom'
import moment from 'moment'
import FeatherIcon from 'feather-icons-react'
import '../score.css'
import {
  checkIsWarning,
} from '../Validation'
import TimePickerInput from '../../TimePicker/TimePickerInput'
import { useTimeFormat } from 'contexts/TimeFormatContext'

const TIME_FORMAT = 'HH:mm'

const inputStyles = {
  textAlign: 'center',
  fontSize: '14px',
}

const AddScore = ({
  uischema,
  scoreIndex,
  saveScore,
  cancelScore,
  scoreList,
  isCreateOnly,
  scoreActionCreateOnly = {},
  warningChart,
}) => {
  const today = moment().format('YYYY-MM-DD')
  const fullConfig = objectPath.get(uischema, 'scoreConfig') || []
  const scoreValues = objectPath.get(uischema, 'scoreValues') || {}
  const scoreSetting = objectPath.get(uischema, 'scoreSetting') || {}
  const { formatTime } = useTimeFormat()
  const addScoreRef = useRef(null)

  const scoreConfig = fullConfig.filter(
    ({ isDate, isTime }) => !isDate && !isTime
  )

  const scoreValueStyle = useMemo(() => {
    if (scoreSetting?.scoreWidth) {
      return {
        width: scoreSetting.scoreWidth,
      }
    }
    return {}
  }, [scoreSetting?.scoreWidth])

  const dateChart = useMemo(() => {
    return fullConfig.find(({ isDate, enabled }) => isDate && enabled) || {}
  }, [])

  const timeChart = useMemo(() => {
    return fullConfig.find(({ isTime, enabled }) => isTime && enabled) || {}
  }, [])

  const [score, setScore] = useState(cloneDeep(scoreValues))

  const { isTime } = timeChart
  const { isDate } = dateChart
  const dateName = objectPath.get(dateChart, 'name') || 'date'
  const timeName = objectPath.get(timeChart, 'name') || 'time'
  const foundDate = objectPath.get(score, dateName)
  const foundTime = objectPath.get(score, timeName)

  const configContext = useContext(ConfigContext)
  const { layoutType } = configContext.state

  const colorByTheme = useMemo(
    () => (layoutType === 'dark' ? '#adb7be' : '#495057'),
    []
  )

  useEffect(() => {
    if (scoreIndex === -1) {
      if (isDate) {
        updateDate(today)
      } else {
        objectPath.del(score, dateName)
      }
      if (isTime) {
        updateTime(moment().format(TIME_FORMAT))
      } else {
        objectPath.del(score, timeName)
      }
      setScore(score)
    } else {
      let score = objectPath.get(scoreList, scoreIndex)
      setScore(score)
    }
  }, [scoreIndex])

  const handleChangeTextField = (name, value) => {
    objectPath.set(score, name, value)
    setRender(true)
    setScore(score)
    if (isCreateOnly) {
      saveScore(scoreIndex, score, isCreateOnly)
    }
  }

  const updateDate = (date) => {
    handleChangeTextField(dateName, date)
  }

  const updateTime = (time) => {
    handleChangeTextField(timeName, time)
  }

  const [render, setRender] = useState(false)

  useEffect(() => {
    if (render) {
      setRender(false)
    }
  }, [render])

  const focusScoreInput = (index) => {
    let input = addScoreRef.current?.querySelector(
      `[data-score-input="${index}"] input`
    )
    if (input) {
      input.focus()
    }
  }

  const handleKeyDownTextField = (e, index) => {
    if (e.key === (scoreSetting.addScoreNextInputKey ?? 'Enter')) {
      e.preventDefault()
      focusScoreInput(index + 1)
    } else if (
      e.key === (scoreSetting.addScorePreviousRowKey ?? 'ArrowUp')
    ) {
      e.preventDefault()
      focusScoreInput(index - 1)
    } else if (e.key === (scoreSetting.addScoreNextRowKey ?? 'ArrowDown')) {
      e.preventDefault()
      focusScoreInput(index + 1)
    } else if (
      e.key === (scoreSetting.addScoreLeftKey ?? 'ArrowLeft') ||
      e.key === (scoreSetting.addScoreRightKey ?? 'ArrowRight')
    ) {
      e.preventDefault()
    }
  }

  console.log('foundTime', foundTime)

  let scoreInputIndex = 0

  return (
    <div ref={addScoreRef}>
      <div
        className={`border border-${scoreIndex === -1 ? 'success' : 'warning'}`}
      >
        {isDate && (
          <div className="body-box d-flex">
            <div className="d-flex border w-100 p-2">
              <DatePickerCustom
                value={foundDate}
                onChange={(date) => updateDate(date.value)}
                formatDisplay={dateChart?.date?.format ?? 'DD/MM/YYYY'}
                placeholder={dateChart?.date?.format ?? 'DD/MM/YYYY'}
                isClearable={true}
                maskSign={dateChart?.date?.maskSign ?? '/'}
                calendar={dateChart?.date?.calendar}
              />
            </div>
          </div>
        )}
        {isTime && (
          <div className="body-box d-flex">
            <div className="d-flex border w-100 p-2">
              <TimePickerInput
                value={foundTime}
                onChange={(e) => {
                  let date = `${today} ${e}`
                  let time = formatTime(date, TIME_FORMAT)
                  updateTime(time)
                }}
                formatDisplay={TIME_FORMAT}
                placeholder={TIME_FORMAT}
                isClearable={true}
              />
            </div>
          </div>
        )}
        {scoreConfig.map((config) => {
          let { group, name, data, enabled } = config
          let scoreValue = objectPath.get(score, name)
          let isWarning = checkIsWarning(warningChart, name, scoreValue)
          if (!enabled) return
          if (group) {
            return (
              <div>
                {data.map(({ name, enabled }) => {
                  if (!enabled) return
                  let scoreValue = objectPath.get(score, name)
                  let inputIndex = scoreInputIndex++
                  return (
                    <div
                      className="sub-box"
                      style={scoreValueStyle}
                      data-score-input={inputIndex}
                    >
                      <TextField
                        inputProps={{
                          style: {
                            ...inputStyles,
                            color: isWarning ? 'red' : colorByTheme,
                          },
                        }}
                        autoComplete="off"
                        value={scoreValue}
                        onChange={(e) =>
                          handleChangeTextField(name, e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDownTextField(e, inputIndex)}
                      />
                    </div>
                  )
                })}
              </div>
            )
          }
          let inputIndex = scoreInputIndex++
          return (
            <div
              className="sub-box"
              style={scoreValueStyle}
              data-score-input={inputIndex}
            >
              <TextField
                inputProps={{
                  style: {
                    ...inputStyles,
                    color: isWarning ? 'red' : colorByTheme,
                  },
                }}
                autoComplete="off"
                value={scoreValue}
                onChange={(e) => handleChangeTextField(name, e.target.value)}
                onKeyDown={(e) => handleKeyDownTextField(e, inputIndex)}
              />
            </div>
          )
        })}
      </div>

      <div className="d-flex align-items-end justify-content-end">
        {isCreateOnly ? (
          <div className="d-flex">
            {scoreActionCreateOnly.isOnAPI ? (
              <div className="mb-2">
                <Button
                  className="score-button"
                  variant="outline-success"
                  onClick={() => saveScore(scoreIndex, score)}
                >
                  <FeatherIcon icon="check" size="16" />
                </Button>
              </div>
            ) : (
              ''
            )}
          </div>
        ) : (
          <div className="d-flex">
            <div className="mb-2">
              <Button
                className="score-button"
                variant="outline-success"
                onClick={() => saveScore(scoreIndex, score)}
              >
                <FeatherIcon icon="check" size="16" />
              </Button>
            </div>
            <div className="mx-1">
              <Button
                className="score-button"
                variant="outline-danger"
                onClick={() => cancelScore()}
              >
                <FeatherIcon icon="x" size="16" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
export default AddScore
